import { ReactElement, useRef } from 'react'
import type MuxPlayerElement from '@mux/mux-player'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { CourseV2VideoStep } from 'src/v3/classroom-v2/types'
import { VideoPlayer } from 'src/v3/classroom-v2/components/video-player'
import { useVideoCompliance } from 'src/v3/classroom-v2/hooks/use-video-compliance'

export type ComplianceVideoStepProps = {
  courseID: string
  video: CourseV2VideoStep
  userID: string
  maxWatchedPositionSeconds: number
  isStepCompleted: boolean
  onProgressMade: (progressSeconds: number) => void
  onCompleted: () => void
  onPriorStepsIncomplete: () => void
}

// The compliance capability, attached to the plain base player. See
// ENG-432/ENG-433: free-roam is this same base player with nothing
// attached, rather than a parallel implementation with checks stripped out.
export let ComplianceVideoStep = ({
  courseID,
  video,
  userID,
  maxWatchedPositionSeconds,
  isStepCompleted,
  onProgressMade,
  onCompleted,
  onPriorStepsIncomplete,
}: ComplianceVideoStepProps): ReactElement => {
  let playerRef = useRef<MuxPlayerElement | null>(null)

  let {
    interruption,
    isPlayerInteractionBlocked,
    violationWarning,
    dismissViolationWarning,
    handleTimeUpdate,
    handleRateChange,
    handleEnded,
    acknowledgeInsufficientProgress,
    acknowledgePriorStepsIncomplete,
    confirmContinuePlayback,
  } = useVideoCompliance({
    courseID,
    videoID: video.id,
    maxWatchedPositionSeconds,
    isStepCompleted,
    onProgressMade,
    onCompleted,
    onPriorStepsIncomplete,
    playerRef,
  })

  return (
    <Box sx={{ position: 'relative' }}>
      <style>
        {`
          mux-player {
            --seek-forward-button: none;
            --playback-rate-button: none;
            --pip-button: none;
          }
        `}
        {isPlayerInteractionBlocked &&
          `
          mux-player {
            pointer-events: none;
            --controls: none;
          }
        `}
      </style>
      <VideoPlayer
        ref={playerRef}
        courseID={courseID}
        video={video}
        userID={userID}
        startTime={maxWatchedPositionSeconds}
        onTimeUpdate={handleTimeUpdate}
        onRateChange={handleRateChange}
        onEnded={handleEnded}
      />

      {interruption.kind === 'countdown' && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10,
          }}
        >
          <Box sx={{ color: 'white', fontSize: '1.25rem', fontWeight: 600 }}>
            Video Paused
          </Box>
        </Box>
      )}

      <Dialog
        open={interruption.kind === 'multiTabDialog'}
        aria-labelledby="multi-tab-dialog-title"
        aria-describedby="multi-tab-dialog-description"
      >
        <DialogTitle id="multi-tab-dialog-title">
          Multiple Video Streams Detected
        </DialogTitle>
        <DialogContent id="multi-tab-dialog-description">
          We have detected video progress events from multiple courses within a
          short time period. For compliance purposes, you can only make progress
          on one course at a time to ensure proper credit hour tracking.
          <br />
          <br />
          If you recently switched from one course to another, please wait a
          moment before continuing. This helps us maintain accurate records of
          your learning time.
          <br />
          <br />
          By clicking &quot;Continue&quot;, you confirm this is the course you
          want to watch. You will need to wait 30 seconds before playback can
          resume.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={confirmContinuePlayback}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={interruption.kind === 'insufficientProgressDialog'}
        aria-labelledby="insufficient-progress-dialog-title"
        aria-describedby="insufficient-progress-dialog-description"
      >
        <DialogTitle id="insufficient-progress-dialog-title">
          Video Cannot Be Completed
        </DialogTitle>
        <DialogContent id="insufficient-progress-dialog-description">
          This video cannot be marked as complete at this time due to
          insufficient evidence of video progress.
          <br />
          <br />
          For compliance purposes, our system tracks viewing patterns to ensure
          proper credit hour tracking. Please watch the video content to
          continue.
          <br />
          <br />
          By clicking &quot;Continue&quot;, you will be returned to your last
          verified progress point.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={acknowledgeInsufficientProgress}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={interruption.kind === 'priorStepsIncompleteDialog'}
        aria-labelledby="prior-steps-incomplete-dialog-title"
        aria-describedby="prior-steps-incomplete-dialog-description"
      >
        <DialogTitle id="prior-steps-incomplete-dialog-title">
          Earlier Steps Not Yet Completed
        </DialogTitle>
        <DialogContent id="prior-steps-incomplete-dialog-description">
          This step can&apos;t be marked complete yet because an earlier step in
          this course hasn&apos;t been completed while recording progress.
          <br />
          <br />
          By clicking &quot;Continue&quot;, you&apos;ll be taken back to the
          first incomplete step.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={acknowledgePriorStepsIncomplete}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={interruption.kind === 'countdown'}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" sx={{ width: '100%' }}>
          {interruption.kind === 'countdown'
            ? `Resuming playback in ${interruption.secondsRemaining} seconds...`
            : null}
        </Alert>
      </Snackbar>

      <Snackbar
        open={violationWarning !== null}
        autoHideDuration={6000}
        onClose={dismissViolationWarning}
      >
        <Alert
          onClose={dismissViolationWarning}
          severity="warning"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {violationWarning === 'speed'
            ? 'Playback speed changes are not allowed'
            : 'Seeking forward is not allowed'}
        </Alert>
      </Snackbar>
    </Box>
  )
}
